import { Box, CircularProgress, Grid, Stack } from "@mui/material";
import { useEffect, useMemo, useState, useRef } from "react";
import { SearchComponent } from "./SearchComponent";
import { WorkerCard } from "../cards/WorkerCard";
import { useAppDispatch, useAppSelector } from "../common/redux/reduxHooks";
import { RootState } from "../common/redux/store";
import { getDisponibilities } from "./redux/searchAction";
import { StructureCard } from "../cards/StructureCard";
import { DisponibiliteStructure } from "../structures/model/StructureModel";
import "./SearchPage.css";
import EmptySearch from "../common/components/EmptySearch";
import LoadingAnimation from "../common/components/LoadingAnimation";
import { useTranslation } from "react-i18next";

export const SearchPage = () => {
    const [page, setPage] = useState(1);
    const [isFetching, setIsFetching] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [filters, setFilters] = useState<Record<string, any>>({});
    const [isSearchEmpty, setIsSearchEmpty] = useState(false);

    const dispatch = useAppDispatch();

    const userRoles = useAppSelector((state) => state.auth.data.roles);
    const userSelector = useAppSelector((state) => state.user.data);
    const userSelect = useAppSelector((state) => state.user.proData);

    const disponibilities = useAppSelector(
        (state: RootState) => state.search.data
    );
    const structures = useAppSelector(
        (state: RootState) => state.search.strucData
    );

    const { t } = useTranslation();
    const itemsPerPage = 20;
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!hasMore) return;

        setIsFetching(true);
        const userRole = userRoles.includes("ROLE_STRUCTURE")
            ? "ROLE_STRUCTURE"
            : "ROLE_PROFESSIONNEL";

        dispatch(
            getDisponibilities({
                page,
                itemsPerPage,
                filters: {
                    ...filters,
                },
                role: userRole,
            })
        )
            .unwrap()
            .then((response: any) => {
                const noProfessionals = response.professionnels?.length === 0;
                const noStructures = response.structures?.length === 0;

                if (page === 1 && noProfessionals && noStructures) {
                    setIsSearchEmpty(true);
                    setHasMore(false);
                } else if (noProfessionals && noStructures) {
                    setHasMore(false);
                } else {
                    setIsSearchEmpty(false);
                }
            })
            .finally(() => setIsFetching(false));
    }, [dispatch, page, itemsPerPage, userRoles, hasMore, filters]);

    useEffect(() => {
        if (!hasMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !isFetching) {
                    setPage((prev) => prev + 1);
                }
            },
            { threshold: 1.0 }
        );

        const currentSentinel = sentinelRef.current;
        if (currentSentinel) observer.observe(currentSentinel);

        return () => {
            if (currentSentinel) observer.unobserve(currentSentinel);
        };
    }, [isFetching, hasMore]);

    const filteredStructures = useMemo(() => {
        return (structures || []).filter(
            (item: DisponibiliteStructure) =>
                item &&
                (!item.isInterested || item.isInterested.status !== "accepted")
        );
    }, [structures]);

    const handleSearchSubmit = (newFilters: Record<string, any>) => {
        setFilters(newFilters);
        setPage(1);
        setHasMore(true);
    };

    const renderCards = (
        data: any[],
        CardComponent: React.ElementType,
        emptyComponent?: React.ReactNode
    ) => (
        <Box
            sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}
        >
            {data.length > 0
                ? data.map((item, i) => (
                      <Grid item key={i} m={1} lg={5}>
                          <CardComponent {...item} />
                      </Grid>
                  ))
                : !isFetching &&
                  page === 1 &&
                  (isSearchEmpty || data.length === 0) &&
                  emptyComponent}
        </Box>
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Stack sx={{ mt: 3 }}>
                <SearchComponent onSearchSubmit={handleSearchSubmit} />
            </Stack>

            {isFetching && page === 1 ? (
                <LoadingAnimation />
            ) : userSelector.societe ? (
                renderCards(filteredStructures, WorkerCard, <EmptySearch />)
            ) : (
                renderCards(
                    disponibilities ?? [],
                    StructureCard,
                    <EmptySearch isJobSearch />
                )
            )}

            <div ref={sentinelRef} />

            {isFetching && page > 1 && (
                <Box sx={{ textAlign: "center", margin: "1rem" }}>
                    <CircularProgress color="inherit" />
                </Box>
            )}

            {!hasMore && page > 1 && !isSearchEmpty && (
                <Box
                    sx={{
                        textAlign: "center",
                        margin: "1rem",
                        color: "grey",
                        fontWeight: "bold",
                    }}
                >
                    {t("loading.complete.message")}
                </Box>
            )}
        </Box>
    );
};
